import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Button from '../../components/Button/Button';
import Modal, { ModalActions, ModalSubtitle, ModalTitle } from '../../components/Modal/Modal';
import { useRescueContext } from '../../lib/RescueContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import {
  createRescueApiKeyMutation,
  regenerateRescueApiKeyMutation,
  rescueApiKeyStatusQuery,
  type CreateRescueApiKeyData,
  type RegenerateRescueApiKeyData,
  type RescueApiKeyStatusData,
} from '../../lib/apiKeyQueries';
import { adaptApiKeyStatus } from '../../lib/apiKeyAdapters';
import AccountSectionCard, { DetailRow } from './AccountSectionCard';
import ApiKeyRevealModal from './ApiKeyRevealModal';
import styles from './Account.module.scss';

// Matches the backend's self-service default; shown before a key exists.
const DEFAULT_RATE_LIMIT_REQ_PER_MIN = 60;

function formatDate(iso: string | null): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function errorMessage(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'Something went wrong — please try again.';
}

/**
 * Self-service API key for the rescue's chip-registration integrations. Issue a first key, regenerate
 * an active one (the only way to see a key's plaintext again), or — once Fi staff have revoked it —
 * point the user at customer support. Verified rescues only; the backend enforces the same gate.
 */
export default function CardApiKey() {
  const { isVerifiedReseller, orgInfoLoading } = useRescueContext();
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading } = useQuery<RescueApiKeyStatusData>(rescueApiKeyStatusQuery, {
    skip: orgInfoLoading || !isVerifiedReseller,
  });
  const view = adaptApiKeyStatus(data);

  // 'no-cache' keeps the plaintext out of the Apollo cache; the status query is refetched instead,
  // since regenerate replaces the key at a new id.
  const mutationOptions = {
    fetchPolicy: 'no-cache' as const,
    refetchQueries: [{ query: rescueApiKeyStatusQuery }],
    awaitRefetchQueries: true,
  };
  const [createKey, { loading: creating }] = useMutation<CreateRescueApiKeyData>(
    createRescueApiKeyMutation,
    mutationOptions,
  );
  const [regenerateKey, { loading: regenerating }] = useMutation<RegenerateRescueApiKeyData>(
    regenerateRescueApiKeyMutation,
    mutationOptions,
  );

  async function handleCreate() {
    setError(null);
    try {
      const result = await createKey();
      const key = result.data?.createRescueApiKey.plaintextKey;
      if (key) {
        setPlaintextKey(key);
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function handleRegenerate() {
    setError(null);
    try {
      const result = await regenerateKey();
      const key = result.data?.regenerateRescueApiKey.plaintextKey;
      if (key) {
        setPlaintextKey(key);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setConfirmingRegenerate(false);
    }
  }

  function renderBody() {
    if (orgInfoLoading) {
      return <div className={styles.loading}>Loading…</div>;
    }
    if (!isVerifiedReseller) {
      return <div className={styles.apiKeyNote}>API access becomes available once your organization is verified.</div>;
    }
    if (loading && !view) {
      return <div className={styles.loading}>Loading…</div>;
    }
    if (!view) {
      return <div className={styles.uploadError}>Couldn't load your API key status.</div>;
    }
    if (view.state === 'REVOKED') {
      return (
        <div className={styles.apiKeyNote}>
          API access for your organization has been disabled. Contact Customer Service at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to re-enable it.
        </div>
      );
    }
    if (view.state === 'NONE') {
      return (
        <>
          <div className={styles.apiKeyNote}>
            Generate a key to connect your systems to Fi. Keys are limited to {DEFAULT_RATE_LIMIT_REQ_PER_MIN} requests
            per minute.
          </div>
          <div className={styles.apiKeyActions}>
            <Button variant="green" size="sm" onClick={handleCreate} disabled={creating}>
              {creating ? 'Generating…' : 'Generate API key'}
            </Button>
          </div>
        </>
      );
    }
    return (
      <>
        <DetailRow label="Key" value={<code className={styles.apiKeyMasked}>{view.maskedKey}</code>} />
        <DetailRow label="Created" value={formatDate(view.createdAt)} />
        <DetailRow label="Last used" value={formatDate(view.lastUsedAt) || 'Never'} />
        <DetailRow label="Rate limit" value={`${view.rateLimitReqPerMin} requests / minute`} />
        <div className={styles.apiKeyActions}>
          <Button variant="greenOutline" size="sm" onClick={() => setConfirmingRegenerate(true)}>
            Regenerate key
          </Button>
        </div>
      </>
    );
  }

  return (
    <AccountSectionCard
      title="API Access"
      description="A private key that lets your own systems register microchips with Fi."
    >
      {renderBody()}
      {error && <div className={styles.uploadError}>{error}</div>}

      <Modal open={confirmingRegenerate} onClose={() => setConfirmingRegenerate(false)}>
        <ModalTitle>Regenerate API key?</ModalTitle>
        <ModalSubtitle>
          Your current key will stop working immediately. Any integration using it must be updated with the new key.
        </ModalSubtitle>
        <ModalActions>
          <Button variant="ghost" onClick={() => setConfirmingRegenerate(false)} disabled={regenerating}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRegenerate} disabled={regenerating}>
            {regenerating ? 'Regenerating…' : 'Regenerate key'}
          </Button>
        </ModalActions>
      </Modal>

      <ApiKeyRevealModal plaintextKey={plaintextKey} onDone={() => setPlaintextKey(null)} />
    </AccountSectionCard>
  );
}
